import axios from 'axios';
import { fetchArticleGallery } from './articleGallery';

// Internal display shapes used by the notification bar.
//  pinned news:  { kind: 'news', title, image, href }
//  seat update:  { kind: 'seat', id, time, message, href }
//
// The future APIs are expected to return { title, image, link, url, ... }.
// The normalizers below already accept that shape as well as the current
// `pins` shape from article_with_gallery.php, so swapping endpoints later is
// a one-line change in fetchPinned()/fetchSeatUpdates().

const getLang = () => localStorage.getItem('lang') || 'zh_CN';

export const normalizePinned = (raw) => {
  if (!raw) return null;
  const title = raw.title || '';
  if (!title) return null;
  return {
    kind: 'news',
    title,
    image: raw.image || raw.picurl || '',
    href: raw.link || raw.url || '',
  };
};

// Live video card for the news-roll drawer: { url, img, title } → { href, image, title }.
// Returns null when there's no URL (so the card hides).
export const normalizeLiveVideo = (raw) => {
  if (!raw || !raw.url) return null;
  return {
    href: raw.url,
    image: raw.img || raw.image || '',
    title: raw.title || '',
  };
};

export const normalizeSeatUpdate = (raw) => {
  if (!raw) return null;
  const message = raw.message || raw.title || '';
  if (!message) return null;
  return {
    kind: 'seat',
    id: raw.id != null ? raw.id : message,
    time: Number(raw.time) || 0,
    message,
    href: raw.link || raw.url || '',
  };
};

// Pinned news. Interim source: the existing `pins` from
// article_with_gallery.php. An editorial override via window.pinnedNotifications
// (see public/config.js) takes precedence so items can be pinned without a deploy.
export const fetchPinned = async () => {
  try {
    if (Array.isArray(window.pinnedNotifications)) {
      return window.pinnedNotifications.map(normalizePinned).filter(Boolean);
    }
    // TODO(future pinned-news API): swap fetchArticleGallery for the dedicated
    // endpoint; normalizePinned already accepts { title, image, link, url }.
    // Shared single-flight cache — on the homepage this resolves from the same
    // request as Home's own data fetch instead of issuing a second one.
    const data = await fetchArticleGallery();
    const pins = (data && data.pins) || [];
    return pins.map(normalizePinned).filter(Boolean);
  } catch {
    return [];
  }
};

// Notification-bar feed from the public-api endpoint (reads the DB directly, so
// the dashboard and public site need not share a filesystem): an admin-entered
// message (`pins`) plus the latest seat-vote update within 10 min (`updates`).
// Polled with a minute-bucketed ?t= so the URL only changes once a minute →
// Cloudflare/browser serve a cached copy between changes.
export const fetchNoti = async () => {
  try {
    const bucket = Math.floor(Date.now() / 20000) * 20; // seconds, 20s-aligned (matches noti.php cache)
    const res = await axios.get(`public-api/noti?lang=${getLang()}&t=${bucket}`);
    const data = res.data || {};
    return {
      notiPinned: (data.pins || []).map(normalizePinned).filter(Boolean),
      updates: (data.updates || []).map(normalizeSeatUpdate).filter(Boolean),
      liveVideo: normalizeLiveVideo(data.livevideo),
    };
  } catch {
    return { notiPinned: [], updates: [], liveVideo: null };
  }
};

// Seat-result-updated alerts. Server-fed only: the bar is global (shows on the
// homepage too) but seatinfo.php is polled only on the seat detail page, so a
// client-side diff cannot drive site-wide alerts. Dormant until the API exists.
export const fetchSeatUpdates = async () => {
  try {
    const endpoint = window.seatUpdatesAPI;
    if (!endpoint) return [];
    // TODO(future seat-updates API): expected response is an array of
    // { id, time, message|title, link|url }.
    const res = await axios.get(endpoint);
    const items = Array.isArray(res.data)
      ? res.data
      : (res.data && res.data.updates) || [];
    return items.map(normalizeSeatUpdate).filter(Boolean);
  } catch {
    return [];
  }
};
